/**
 * @module hooks/useChartIconVisibility
 * @description 7 段判定齿轮图标是否可见（Phase 7 接入 V2 Context 后完善）
 *
 * 仅依赖 DVA Query，不订阅 ChartSettings draft。
 */
import { useMemo } from 'react';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import { useQuerySelector } from './deps';
import { toChartTypeEnum } from '../utils/toChartTypeEnum';
import { EMPTY_COLUMN_INFO_LIST } from '../utils/constants';

/** 表格齿轮显隐：聚合看维/指，明细看 columnRawColumns（与 PRD 4.4 / checkRawColumnModeConditions 一致） */
export function isTableChartIconVisible(
  mode: TChartCalculateMode,
  dimCount: number,
  metCount: number,
  rawColCount: number,
): boolean {
  if (mode === ChartCalculateMode.RawColumn) {
    return rawColCount >= 1;
  }
  return dimCount >= 1 || metCount >= 1;
}

export function resolveChartIconVisibility(
  chartType: TChartType | undefined,
  mode: TChartCalculateMode,
  dimCount: number,
  metCount: number,
  rawColCount: number,
): boolean {
  if (chartType === ChartType.BigNumber || chartType === ChartType.Trend) {
    return false;
  }

  if (chartType === ChartType.Table) {
    return isTableChartIconVisible(mode, dimCount, metCount, rawColCount);
  }

  const alwaysShow = [
    ChartType.Line,
    ChartType.Bar,
    ChartType.Pie,
    ChartType.Map,
  ];
  if (alwaysShow.includes(chartType)) {
    return dimCount >= 1 || metCount >= 1;
  }

  if (chartType === ChartType.StackBar) {
    return dimCount >= 1 && metCount >= 1;
  }

  if (chartType === ChartType.MixedChart) {
    return dimCount >= 1 && metCount >= 2;
  }

  if (
    chartType === ChartType.HorizontalBarChart ||
    chartType === ChartType.PercentStackBar ||
    chartType === ChartType.PercentLine
  ) {
    return dimCount >= 1 && metCount >= 1;
  }

  if (chartType === ChartType.WordCloud) {
    return dimCount >= 1 && metCount >= 1;
  }

  if (chartType === ChartType.EventLine) {
    return dimCount >= 1;
  }

  return false;
}

export function useChartIconVisibility(): boolean {
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const mode = useQuerySelector(
    s => (s.mode as TChartCalculateMode) ?? ChartCalculateMode.Aggregate,
  );
  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnRawColumns = useQuerySelector(
    s => s.columnRawColumns ?? EMPTY_COLUMN_INFO_LIST,
  );

  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const dimCount = columnDimensions.length;
  const metCount = columnMetrics.length;
  const rawColCount = columnRawColumns.length;

  return useMemo(
    () =>
      resolveChartIconVisibility(
        chartType,
        mode,
        dimCount,
        metCount,
        rawColCount,
      ),
    [chartType, mode, dimCount, metCount, rawColCount],
  );
}

/**
 * 外部消费别名 — AnalysisLineChart 等 Panel 外部组件使用
 *
 * @see useChartIconVisibility
 */
export function useChartIconVisible(): boolean {
  return useChartIconVisibility();
}

